// MOS — The permission catalogue: every board, sub-board and action a person
// can be given, and what each access profile gets by default.
//
// THE ONE LIST. A board that isn't here cannot be granted, and `VIEW_PERMISSION`
// is typed `Record<ViewType, …>` so TypeScript fails the build the moment a new
// view appears without a permission — the same trick that kept VIEW_MIN_TIER
// honest for a year.
//
// ── The model (Drew, 2026-09-11) ────────────────────────────────────────────
//  1. Every employee sits on ONE access profile (owner / operations / sales /
//     estimating / pm / drafting / plant-lead / plant-floor). The profile is
//     the default for every key.
//  2. Per-person overrides are stored as EXCEPTIONS only, so editing a profile
//     still flows through everything nobody touched by hand.
//  3. Money is not a board — it's a lens across all of them. See moneyFields.ts.
//
// ── Inheritance ─────────────────────────────────────────────────────────────
// A child key with no grant of its own inherits its parent's level, so
// `board.plant.batches` follows `board.plant` until somebody says otherwise.
// A BOARD is visible in the nav when anything in its subtree is visible: an
// estimator who holds only Accounting · AR sees the Accounting board carrying
// one tab, not a locked door.
//
// Mirror: supabase/functions/make-server-e3a97ae5/_shared/permission-catalog.ts
// — Deno and the browser cannot import each other, so the two files are checked
// against one another by `npm run test:permissions`. Change one, change both.

import type { ViewType } from '../types/views';

/** What a person may do with a board. `read` = can look, can't change. */
export type PermLevel = 'none' | 'read' | 'full';

/** How much money renders for this person, anywhere in MOS. */
export type MoneyLens = 'none' | 'job' | 'cost' | 'company';

export type ProfileKey =
  | 'owner' | 'operations' | 'sales' | 'estimating'
  | 'pm' | 'drafting' | 'plant-lead' | 'plant-floor';

export const PROFILE_KEYS: readonly ProfileKey[] = [
  'owner', 'operations', 'sales', 'estimating', 'pm', 'drafting', 'plant-lead', 'plant-floor',
] as const;

export interface ProfileDef {
  key: ProfileKey;
  label: string;
  /** Plain-English who-this-is, shown under the profile picker. */
  blurb: string;
  /** The legacy tier this profile computes to, so 726 requireTier gates keep working. */
  tier: 1 | 2 | 3;
  /** Money rung everyone on this profile gets unless overridden per person. */
  money: MoneyLens;
}

export const PROFILES: readonly ProfileDef[] = [
  { key: 'owner',       label: 'Owner',          blurb: 'Everything, including the books and people records.', tier: 3, money: 'company' },
  { key: 'operations',  label: 'Operations',     blurb: 'Runs the office — every board, the books, the paperwork.', tier: 3, money: 'company' },
  { key: 'sales',       label: 'Sales',          blurb: 'Chases the work, and prices it — the Estimator is theirs.', tier: 2, money: 'cost' },
  { key: 'estimating',  label: 'Estimating',     blurb: 'Prices the work. Cost and margin, and the Estimator.', tier: 2, money: 'cost' },
  { key: 'pm',          label: 'PM',             blurb: 'Runs jobs after the award — every board plus purchasing.', tier: 3, money: 'company' },
  { key: 'drafting',    label: 'Drafting & CNC', blurb: 'Draws and programs the work.', tier: 2, money: 'company' },
  { key: 'plant-lead',  label: 'Plant Lead',     blurb: 'Runs the floor — the schedule, the batches, the mixes.', tier: 1, money: 'none' },
  { key: 'plant-floor', label: 'Plant Floor',    blurb: 'Makes the work. Quantities, dates and status — no dollars.', tier: 1, money: 'none' },
] as const;

export interface PermNode {
  key: string;
  label: string;
  /** Parent key. A node with no grant of its own inherits the parent's level. */
  parent?: string;
  /** Board this node belongs to — drives the colour on the access screen. */
  board?: string;
  /** True when this node is one of the eight boards in the nav. */
  isBoard?: boolean;
  /** Shown beside the row on the access screen when the row needs explaining. */
  note?: string;
}

// ── The tree ────────────────────────────────────────────────────────────────
// Order here is the order the access screen renders.
export const PERMISSIONS: readonly PermNode[] = [
  { key: 'board.command-center', label: 'Command Center', isBoard: true, board: 'command-center' },

  { key: 'board.estimating',           label: 'Estimating board', isBoard: true, board: 'estimating' },
  { key: 'board.estimating.feedback',  label: 'Production feedback', parent: 'board.estimating', board: 'estimating' },
  { key: 'board.estimating.settings',  label: 'Estimating settings', parent: 'board.estimating', board: 'estimating' },

  { key: 'board.sales',                 label: 'Sales board', isBoard: true, board: 'sales' },
  { key: 'board.sales.pipeline-health', label: 'Pipeline Health', parent: 'board.sales', board: 'sales' },
  { key: 'board.sales.marketing',       label: 'Marketing', parent: 'board.sales', board: 'sales' },
  { key: 'board.sales.buddy',           label: 'Buddy Building', parent: 'board.sales', board: 'sales' },
  { key: 'board.sales.contacts',        label: 'Contacts', parent: 'board.sales', board: 'sales', note: 'The shared people list — customers, architects, GCs.' },
  { key: 'board.sales.analytics',       label: 'Sales scorecard', parent: 'board.sales', board: 'sales', note: 'The header dashboard on the Sales Board and the full scorecard it opens.' },
  // Andrea's executive view (Drew, 2026-11-04): sales trends against the
  // 2027 plan, reached from inside the scorecard. Owners, operations and PM
  // by default (Drew, Elyce, Andrea); a Monday head is granted per person.
  { key: 'board.sales.executive',       label: 'Sales executive view', parent: 'board.sales', board: 'sales', note: 'Sales trends against the 2027 plan, opened from the scorecard. Dollars still follow the Money dial.' },
  // The year's revenue (QuickBooks) on the Sales header and scorecard. The
  // books' own figure, so only those who see the books by default; Drew
  // opened it to Cassandra per person (2026-11-04: "Yes, show her").
  { key: 'board.sales.revenue',         label: 'Revenue on the Sales scorecard', parent: 'board.sales', board: 'sales', note: "The year's revenue from QuickBooks, against its goal, on the Sales header and scorecard. The rest of the scorecard does not need it." },
  { key: 'board.sales.settings',        label: 'Sales settings', parent: 'board.sales', board: 'sales' },

  { key: 'board.pm', label: 'PM board', isBoard: true, board: 'pm' },

  { key: 'board.drafting',           label: 'Drafting board', isBoard: true, board: 'drafting' },
  { key: 'board.drafting.lifecycle', label: 'Drawing lifecycle', parent: 'board.drafting', board: 'drafting' },
  { key: 'board.drafting.settings',  label: 'Drafting settings', parent: 'board.drafting', board: 'drafting' },

  { key: 'board.plant',              label: 'Plant board', isBoard: true, board: 'plant' },
  { key: 'board.plant.tracking',     label: 'Tracking (TABS)', parent: 'board.plant', board: 'plant' },
  { key: 'board.plant.inventory',    label: 'Inventory', parent: 'board.plant', board: 'plant' },
  { key: 'board.plant.mix-designs',  label: 'Mix Designs', parent: 'board.plant', board: 'plant' },
  { key: 'board.plant.scheduler',    label: 'Scheduler', parent: 'board.plant', board: 'plant' },
  { key: 'board.plant.settings',     label: 'Plant settings', parent: 'board.plant', board: 'plant' },

  { key: 'board.freight',            label: 'Freight board', isBoard: true, board: 'freight', note: 'Everything after Packaging: jobs ready to ship, quote requests to carriers, booked trucks.' },

  { key: 'board.accounting',         label: 'Accounting board', isBoard: true, board: 'accounting', note: 'The board appears when any section below does.' },
  { key: 'board.accounting.ar',      label: 'AR — billing & collections', parent: 'board.accounting', board: 'accounting' },
  { key: 'board.accounting.books',   label: 'The Books, AP, Bank, Tax, Close, CFO', parent: 'board.accounting', board: 'accounting' },
  { key: 'board.accounting.waiting', label: 'Waiting on you', parent: 'board.accounting', board: 'accounting' },

  { key: 'board.initiatives', label: 'Initiatives board', isBoard: true, board: 'initiatives', note: 'Internal projects and initiatives. Everyone sees what is not private; owners edit their own.' },

  { key: 'board.company',                label: 'Company', isBoard: true, board: 'company' },
  { key: 'board.company.hr',             label: 'HR — my own profile, PTO, timecard', parent: 'board.company', board: 'company' },
  { key: 'board.company.hr-admin',       label: 'HR — Team Admin, workforce, punches', parent: 'board.company', board: 'company' },
  { key: 'board.company.hr-applicants',  label: 'HR — job applicants', parent: 'board.company', board: 'company', note: 'Applications from the website: personnel records. Look-only reads them and their résumés; full also marks a status, adds notes and lets a held post in.' },
  { key: 'board.company.sops',           label: 'Standard Ops', parent: 'board.company', board: 'company' },
  { key: 'board.company.autocomms',      label: 'AutoComms', parent: 'board.company', board: 'company' },
  { key: 'board.company.analytics',      label: 'Analytics & Reports', parent: 'board.company', board: 'company' },
  { key: 'board.company.doc-templates',  label: 'Document Templates', parent: 'board.company', board: 'company' },
  { key: 'board.company.settings',       label: 'Company Settings', parent: 'board.company', board: 'company' },
  { key: 'board.automations',            label: 'Automations', parent: 'board.company', board: 'company', note: 'Every agent and connection — it can pause the department. Lives under Company since 2026-10-17.' },

  { key: 'board.improve', label: 'Improve board', isBoard: true, board: 'improve' },

  { key: 'job',            label: 'Job pages', board: 'job', note: 'The job page itself. Its tabs sit under it.' },
  { key: 'job.bidders',    label: 'Job · Bidders', parent: 'job', board: 'job' },
  { key: 'job.submittals', label: 'Job · Submittals', parent: 'job', board: 'job' },
  { key: 'job.files',      label: 'Job · Files', parent: 'job', board: 'job' },
  // Mark, 2026-10-14: the plant does not read the customer's mail. Without
  // this a person still has the job's Activity log tab (the machines' moves);
  // the server leaves the emails out of the answer (_shared/job-comms.ts).
  { key: 'job.comms',      label: 'Job · Emails & activities', parent: 'job', board: 'job', note: 'The emails, calls, texts and notes on a job. Without it the Activity log tab still shows what happened to the job.' },
  // No 'read' state in practice: the Estimator module has no read-only mode,
  // so a viewer who could open it but not save would lose work silently.
  // Everyone who holds this key holds it at full — which is exactly who had
  // the tab before the model changed (Drew, 2026-09-12: "no sales has estimator").
  //
  // The label leads with THE ESTIMATOR because that is what people call it. It
  // read "Job · Estimator & Planner" for two days and Drew, looking for exactly
  // this row, granted the Estimating BOARD twice instead — different key, and
  // Adam still could not open a bid. A row nobody can find is a row nobody has.
  { key: 'job.estimator',  label: 'The Estimator (on a job)', parent: 'job', board: 'job', note: 'The pricing tab inside a job — not the Estimating board. Opening a bid means pricing it, so there is no look-but-do-not-touch here.' },
  // Split off the Estimator on 2026-09-14 (Drew: "Planner will have different
  // access levels than estimator as the plant lead and even just plant floor
  // could want to look at the planner"). It shares the Estimator's PARTS but
  // carries no money of its own — it is a production schedule — so it is the
  // one job tab the plant can be given without giving them a bid.
  { key: 'job.planner',    label: 'The Production Planner (on a job)', parent: 'job', board: 'job', note: 'The line schedule for a job. Read-only means you can look at the plan without moving anything on it.' },

  { key: 'act.job.delete',         label: 'Delete a job', board: 'action' },
  { key: 'act.priority.override',  label: 'Override the priority order', board: 'action' },
  { key: 'act.dept-config.edit',   label: 'Edit department configuration', board: 'action' },
  { key: 'act.sent-log.view',      label: 'Read the Sent log', board: 'action', note: 'Bid covers and signed quote PDFs.' },
  { key: 'act.metrics.view',       label: 'See throughput metrics', board: 'action' },
  { key: 'act.users.manage',       label: 'Manage people and access', board: 'action' },
  { key: 'act.settings.edit',      label: 'Edit system settings', board: 'action' },
  // Mark, 2026-10-23: the Scheduler's "Email buyers" — one message to the
  // buyer on each job picked off the list. A manager's door: owner,
  // operations and PM by default; anyone else per person.
  { key: 'act.job-email-campaign', label: 'Email the buyers on several jobs (Scheduler)', board: 'action', note: 'The Email buyers button on the Scheduler: pick jobs off the list and send one message to the buyer on each, logged on every job.' },
  { key: 'act.inventory-status',  label: 'Make an inventory item Active or Inactive', board: 'action', note: 'The admin button on an item: it asks why both ways. An inactive item is never ordered until it is brought back.' },
  { key: 'act.mix-formula.edit',  label: 'Change a mix formula', board: 'action', note: 'The amounts and pigment percents on a mix. The floor reads the formula and batches from it; a PM or higher changes it.' },
  // Garrett, 2026-10-27: Tommy (Mold Building) could not see Rebalance
  // remaining, which sat behind full Planner access. The floor may re-cut the
  // lines for what is left on the molds already made, and nothing else on the
  // plan: the rebalance never asks for a set, a mold or a mother mold.
  { key: 'act.planner.rebalance', label: 'Rebalance what is left on the Planner', board: 'action', note: 'The Rebalance remaining button: re-plans the stones still to cast on the molds, sets and mother molds the job already has. It changes nothing else on the plan and never asks for a new mold.' },
  { key: 'data.pay-people',        label: 'Pay & people records', board: 'action', note: 'Salary, pay rate, SSN, licence, home address, date of birth. No money rung grants this.' },
] as const;

const BY_KEY = new Map<string, PermNode>(PERMISSIONS.map(p => [p.key, p]));

export function permNode(key: string): PermNode | undefined {
  return BY_KEY.get(key);
}

/** Children of a key, in catalogue order. */
export function permChildren(key: string): PermNode[] {
  return PERMISSIONS.filter(p => p.parent === key);
}

// ── Profile grants ──────────────────────────────────────────────────────────
// Columns, in PROFILE_KEYS order:
//   owner · operations · sales · estimating · pm · drafting · plant-lead · plant-floor
// Written as a compact 8-character string so the whole matrix is readable at a
// glance and a typo shows up as a wrong-length row (the goldens check that).
//   - = none   r = read-only   f = full
const G = (row: string): Record<ProfileKey, PermLevel> => {
  const out = {} as Record<ProfileKey, PermLevel>;
  PROFILE_KEYS.forEach((p, i) => {
    const c = row[i];
    out[p] = c === 'f' ? 'full' : c === 'r' ? 'read' : 'none';
  });
  return out;
};

/**
 * Drew's grid, 2026-09-11. Keys absent here inherit their parent; a key with no
 * parent and no grant is `none` for everyone.
 */
export const PROFILE_GRANTS: Record<string, Record<ProfileKey, PermLevel>> = {
  //                                  own ops sal est pm  drf ld  flr
  'board.command-center':        G('ffrrr---'),
  'board.estimating':            G('fffff---'),
  'board.sales':                 G('fffff---'),
  'board.sales.pipeline-health': G('ffff----'),
  'board.sales.marketing':       G('ffff----'),
  'board.sales.executive':       G('ff--f---'),
  'board.sales.revenue':         G('ff--f---'),
  'board.sales.contacts':        G('fffffr--'),
  'board.pm':                    G('ffffffr-'),
  'board.drafting':              G('ffffffr-'),
  'board.plant':                 G('ffrffffr'),
  'board.plant.tracking':        G('ffrfffff'),
  'board.plant.inventory':       G('fffffffr'),
  'board.plant.mix-designs':     G('ffrffffr'),
  // Drew, 2026-10-19: Elyce, the sales and pm profiles work the Freight board.
  'board.freight':               G('fff-f---'),
  'board.accounting':            G('--------'),
  'board.accounting.ar':         G('ffrff---'),
  'board.accounting.books':      G('ff------'),
  'board.accounting.waiting':    G('ff------'),
  'board.initiatives':           G('fffffffr'),
  'board.automations':           G('fr------'),
  'board.company':               G('--------'),
  'board.company.hr':            G('ffffffff'),
  'board.company.hr-admin':      G('ff--r-r-'),
  // Job applicants (2026-10-07, the lead's call pending Drew's): owner,
  // operations and PM in full — the person who runs hiring is on the PM
  // profile and must mark statuses and write notes. Nobody else by default;
  // a plant lead who interviews is granted per person.
  'board.company.hr-applicants': G('ff--f---'),
  'board.company.sops':          G('fffffrff'),
  'board.company.autocomms':     G('fffff---'),
  'board.company.analytics':     G('fffffrr-'),
  'board.company.doc-templates': G('fffff---'),
  'board.company.settings':      G('fr------'),
  'board.improve':               G('fffffffr'),
  'job':                         G('ffffffrr'),
  'job.comms':                   G('ffffff--'),
  'job.estimator':               G('ffffff--'),
  //                                  own ops sal est pm  drf ld  flr
  'job.planner':                 G('ffffffrr'),
  'act.job.delete':              G('fffff---'),
  'act.priority.override':       G('ff--f---'),
  'act.dept-config.edit':        G('ffffffr-'),
  'act.sent-log.view':           G('fffff---'),
  'act.metrics.view':            G('ffffffr-'),
  'act.users.manage':            G('ff------'),
  'act.settings.edit':           G('fr------'),
  'act.job-email-campaign':      G('ff--f---'),
  // Drew, 2026-10-26: "no one should be able to ... inactivate an item unless it's an admin. So probably me or you."
  'act.inventory-status':        G('f-------'),
  // Drew, 2026-10-27: "Editing the formula is something that should not be able to be done by the floor. You should be a project manager or higher."
  'act.mix-formula.edit':        G('ff--f---'),
  // Garrett, 2026-10-27: the Mold Building and Production (Casting) screens too.
  'act.planner.rebalance':       G('ffffffff'),
  'data.pay-people':             G('ff------'),
};

// ── View and tab wiring ─────────────────────────────────────────────────────
/**
 * Which permission gates each top-level view. `Record<ViewType, …>` means a new
 * view without a permission is a compile error, not a hole.
 */
export const VIEW_PERMISSION: Record<ViewType, string> = {
  'dashboard':            'board.command-center',
  'command-center':       'board.command-center',
  'sales-workbench':      'board.sales',
  'estimating-workbench': 'board.estimating',
  'drafting-workbench':   'board.drafting',
  'plant-workbench':      'board.plant',
  'pm-workbench':         'board.pm',
  'ar-workbench':         'board.accounting',
  'freight-workbench':    'board.freight',
  'job-details':          'job',
  'improve':              'board.improve',
  'initiatives':          'board.initiatives',
  'automations':          'board.automations',
  'company':              'board.company',
  'hr':                   'board.company.hr',
  'standard-procedures':  'board.company.sops',
  'auto-comms':           'board.company.autocomms',
  'analytics':            'board.company.analytics',
  'doc-templates':        'board.company.doc-templates',
  'settings':             'board.company.settings',
  'contacts':             'board.sales.contacts',
  'scheduling':           'board.plant.scheduler',
  'global-tabs':          'board.plant.tracking',
};

/**
 * Which permission gates each BOARD's cards, keyed the way the boards are
 * named on the wire (the search pills, the job page's Cards panel). The job
 * page is reachable by every profile ('job' above) while the PM board is
 * not, so a job's cards are gated per board — on the server by
 * GET /jobs/:id/cards, in the browser by the panel — and never by the page.
 * Accounting's cards are the AR section's, not the board umbrella (which no
 * profile holds directly).
 */
export const BOARD_PERMISSION: Record<'estimating' | 'sales' | 'pm' | 'drafting' | 'plant' | 'freight' | 'ar', string> = {
  estimating: 'board.estimating',
  sales:      'board.sales',
  pm:         'board.pm',
  drafting:   'board.drafting',
  plant:      'board.plant',
  freight:    'board.freight',
  ar:         'board.accounting.ar',
};

/**
 * Tab-level wiring. A tab that isn't listed follows its view's permission.
 * Several Accounting sections share one key on purpose — they are one money
 * surface wearing seven labels.
 */
export const TAB_PERMISSION: Record<string, Record<string, string>> = {
  'job-details': {
    details:       'job',
    bidders:       'job.bidders',
    submittals:    'job.submittals',
    tabs:          'board.plant.tracking',
    files:         'job.files',
    estimator:     'job.estimator',
    planner:       'job.planner',
  },
  'sales-workbench': {
    projects:           'board.sales',
    'my-day':           'board.sales',
    pipeline:           'board.sales',
    'post-award':       'board.sales',
    chase:              'board.sales',
    'pipeline-health':  'board.sales.pipeline-health',
    'buddy-building':   'board.sales.buddy',
    marketing:          'board.sales.marketing',
    contacts:           'board.sales.contacts',
    analytics:          'board.sales.analytics',
    scorecard:          'board.sales.analytics',
    executive:          'board.sales.executive',
    settings:           'board.sales.settings',
  },
  'estimating-workbench': {
    board:     'board.estimating',
    feedback:  'board.estimating.feedback',
    settings:  'board.estimating.settings',
  },
  'drafting-workbench': {
    board:      'board.drafting',
    lifecycle:  'board.drafting.lifecycle',
    settings:   'board.drafting.settings',
  },
  'plant-workbench': {
    pipeline:        'board.plant',
    batches:         'board.plant',
    offload:         'board.plant',
    tracking:        'board.plant.tracking',
    inventory:       'board.plant.inventory',
    'mix-designs':   'board.plant.mix-designs',
    marc2:           'board.plant',
    status:          'board.plant',
    'water-spider':  'board.plant',
    scheduler:       'board.plant.scheduler',
    settings:        'board.plant.settings',
  },
  'ar-workbench': {
    ar:           'board.accounting.ar',
    'money-desk': 'board.accounting.books',
    workflow:     'board.accounting.books',
    waiting:      'board.accounting.waiting',
    books:        'board.accounting.books',
    ap:           'board.accounting.books',
    bank:         'board.accounting.books',
    tax:          'board.accounting.books',
    close:        'board.accounting.books',
    cfo:          'board.accounting.books',
    ideas:        'board.accounting.books',
  },
  'command-center': {
    health:     'board.command-center',
    pm:         'board.pm',
    ar:         'board.accounting.ar',
    executive:  'board.command-center',
  },
  'automations': {
    agents:       'board.automations',
    connections:  'board.automations',
    books:        'board.automations',
  },
  // The rest of HR follows the view's key; the applicants list is personnel
  // data with its own key (the server's /hr/applicants doors ask the same one).
  'hr': {
    applicants:   'board.company.hr-applicants',
  },
};

// ── The resolver ────────────────────────────────────────────────────────────
export interface AccessGrantSource {
  profile: ProfileKey;
  /** Per-person exceptions, keyed by permission key. */
  overrides?: Record<string, PermLevel>;
  /**
   * Profile-level deltas from the database — the rows written when somebody
   * retunes a profile on the access screen. Absent keys fall through to the
   * catalogue below, which is why nothing was ever seeded into that table.
   */
  profileGrants?: Record<string, PermLevel>;
  /** Per-person money rung, when it differs from the profile's. */
  moneyOverride?: MoneyLens | null;
  /**
   * The rung as the database resolved it, when known (the server's profile
   * row, the browser's /access/me answer). Absent → `moneyLensFor`.
   */
  moneyLens?: MoneyLens | null;
}

// ── Boards that are nothing but money ───────────────────────────────────────
/**
 * A board whose every screen is money opens only for a lens that reaches that
 * money. The grant and the lens are two dials on the access screen; on these
 * boards the lens decides, so granting the Books to someone with no dollars
 * cannot put the bank on their screen (Drew, 2026-10-23: "make sure that money
 * is not displayed to people that do not have permission to see it"). A child
 * key inherits its parent's floor. Both sides ask it, because it lives inside
 * resolveLevel: the nav, the tabs and requirePermission agree.
 */
export const MONEY_FLOOR: Record<string, MoneyScope> = {
  'job.estimator':            'cost',
  'board.accounting.ar':      'job',
  'board.accounting.books':   'company',
  'board.accounting.waiting': 'company',
};

/** The floor this key sits under, its own or its nearest parent's, or null. */
export function moneyFloorOf(key: string): MoneyScope | null {
  const seen = new Set<string>();
  let node: string | undefined = key;
  while (node && !seen.has(node)) {
    seen.add(node);
    const floor = MONEY_FLOOR[node];
    if (floor) return floor;
    node = BY_KEY.get(node)?.parent;
  }
  return null;
}

/** True when this person's money lens stops below the key's floor. */
export function moneyFloorBlocks(src: AccessGrantSource, key: string): boolean {
  const floor = moneyFloorOf(key);
  return floor !== null && !lensReaches(moneyLensFor(src), floor);
}

const LEVEL_RANK: Record<PermLevel, number> = { none: 0, read: 1, full: 2 };

/** True when `have` is at least `need`. */
export function levelMeets(have: PermLevel, need: PermLevel): boolean {
  return LEVEL_RANK[have] >= LEVEL_RANK[need];
}

export function profileDef(profile: ProfileKey): ProfileDef {
  return PROFILES.find(p => p.key === profile) ?? PROFILES[PROFILES.length - 1];
}

export function isProfileKey(value: unknown): value is ProfileKey {
  return typeof value === 'string' && PROFILE_KEYS.includes(value as ProfileKey);
}

/**
 * The profile a person sits on. A row that predates the model — or one an
 * importer created — falls back to its old tier, so nobody silently gains a
 * board or loses one on the day this shipped.
 */
export function profileForEmployee(
  employee: { accessProfile?: string | null; access_profile?: string | null; accessTier?: number; access_tier?: number } | null | undefined,
): ProfileKey {
  const named = employee?.accessProfile ?? employee?.access_profile;
  if (isProfileKey(named)) return named;
  const tier = employee?.accessTier ?? employee?.access_tier ?? 1;
  return tier >= 3 ? 'owner' : tier === 2 ? 'sales' : 'plant-floor';
}

/**
 * The level this person holds on one key: their own override, else the
 * profile's delta, else the catalogue's default, else the same three questions
 * of the parent key, else none.
 *
 * The edge function's resolveServerLevel walks the identical ladder from the
 * identical catalogue, which is what stops the screen and the server from
 * disagreeing about what somebody may do.
 *
 * A board that is nothing but money answers `none` to a lens below its floor,
 * whatever the grant says (MONEY_FLOOR).
 */
export function resolveLevel(src: AccessGrantSource, key: string): PermLevel {
  if (moneyFloorBlocks(src, key)) return 'none';
  return grantLevel(src, key);
}

/**
 * What the grants alone say, before any money floor: the access screen edits
 * this, so a row keeps the level Drew chose while it waits for a money rung.
 */
export function grantLevel(src: AccessGrantSource, key: string): PermLevel {
  const seen = new Set<string>();
  let node: string | undefined = key;
  while (node && !seen.has(node)) {
    seen.add(node);
    const override = src.overrides?.[node];
    if (override) return override;
    const delta = src.profileGrants?.[node];
    if (delta) return delta;
    const grant = PROFILE_GRANTS[node]?.[src.profile];
    if (grant) return grant;
    node = BY_KEY.get(node)?.parent;
  }
  return 'none';
}

export function can(src: AccessGrantSource, key: string, need: PermLevel = 'read'): boolean {
  return levelMeets(resolveLevel(src, key), need);
}

/**
 * The level a board shows at in the nav: the most open thing inside it. An
 * estimator who holds only Accounting · AR still finds the Accounting board.
 */
export function subtreeLevel(src: AccessGrantSource, key: string): PermLevel {
  let best = resolveLevel(src, key);
  for (const child of PERMISSIONS) {
    if (child.parent !== key) continue;
    const sub = subtreeLevel(src, child.key);
    if (LEVEL_RANK[sub] > LEVEL_RANK[best]) best = sub;
  }
  return best;
}

export function canViewViewPerm(src: AccessGrantSource, view: ViewType): boolean {
  return subtreeLevel(src, VIEW_PERMISSION[view]) !== 'none';
}

export function canViewTabPerm(src: AccessGrantSource, view: ViewType, tabKey: string): boolean {
  const key = TAB_PERMISSION[view]?.[tabKey] ?? VIEW_PERMISSION[view];
  return subtreeLevel(src, key) !== 'none';
}

export function moneyLensFor(src: AccessGrantSource): MoneyLens {
  return src.moneyLens ?? src.moneyOverride ?? profileDef(src.profile).money;
}

export function canSeePayPeople(src: AccessGrantSource): boolean {
  return resolveLevel(src, 'data.pay-people') !== 'none';
}

/** The legacy 1/2/3 an access profile computes to, so old gates keep working. */
export function tierForProfile(profile: ProfileKey): 1 | 2 | 3 {
  return profileDef(profile).tier;
}

// ── Money ───────────────────────────────────────────────────────────────────
// Which rung a figure belongs to. Every dollar render goes through <Money>,
// which reads this; the server reads the same list to strip figures out of a
// payload before it answers (_shared/money-redact.ts).
//
// The list is an ALLOW-LIST of known money field names — a key that isn't here
// is never touched, so `quantity` and `piece_count` travel untouched. Both
// spellings are listed because the wire is snake_case and the UI is camelCase.

/** A figure's rung. There is no 'none' scope — that is the absence of a figure. */
export type MoneyScope = 'job' | 'cost' | 'company';

const LENS_RANK: Record<MoneyLens, number> = { none: 0, job: 1, cost: 2, company: 3 };
const SCOPE_RANK: Record<MoneyScope, number> = { job: 1, cost: 2, company: 3 };

/** True when this lens reaches that rung of figure. */
export function lensReaches(lens: MoneyLens, scope: MoneyScope): boolean {
  return LENS_RANK[lens] >= SCOPE_RANK[scope];
}

export const MONEY_FIELDS: Record<string, MoneyScope> = {
  // ── What a job sells for ──
  bid_price: 'job',                   bidPrice: 'job',
  lost_bid_price: 'job',              lostBidPrice: 'job',
  declined_bid_price: 'job',          declinedBidPrice: 'job',
  bid_release_price: 'job',           bidReleasePrice: 'job',
  ar_invoice_amount: 'job',           arInvoiceAmount: 'job',
  ar_paid_amount: 'job',              arPaidAmount: 'job',
  invoice_amount: 'job',              invoiceAmount: 'job',
  quoted_amount: 'job',               quotedAmount: 'job',
  quote_total: 'job',                 quoteTotal: 'job',
  contract_value: 'job',              contractValue: 'job',
  po_value: 'job',                    poValue: 'job',
  amount_due: 'job',                  amountDue: 'job',
  balance_due: 'job',                 balanceDue: 'job',
  co_price: 'job',                    coPrice: 'job',
  total_price: 'job',                 totalPrice: 'job',
  sell_price: 'job',                  sellPrice: 'job',
  // The award record's sold price, and the freight a bid quoted inside its
  // price — the two figures the freight answer moves between (2026-10-07).
  // Never a bare `freight`: that key is the job panel's freight-card object.
  sold_price: 'job',                  soldPrice: 'job',
  previous_sold_price: 'job',         previousSoldPrice: 'job',
  quoted_freight: 'job',              quotedFreight: 'job',
  freight_not_added: 'job',           freightNotAdded: 'job',
  // A job's worth and what is still owed on it, under the names the boards
  // and the AR views carry them (2026-10-23: the Plant board's popout showed
  // a plant lead "VALUE $10K" — `jobValue` was never on this list).
  job_value: 'job',                   jobValue: 'job',
  bid_value: 'job',                   bidValue: 'job',
  final_price: 'job',                 finalPrice: 'job',
  desired_job_price: 'job',           desiredJobPrice: 'job',
  stock_list_price_per_part: 'job',   stockListPricePerPart: 'job',
  bid_price_at_send: 'job',           bidPriceAtSend: 'job',
  lost_after_award_value: 'job',      lostAfterAwardValue: 'job',
  ar_open_balance: 'job',             arOpenBalance: 'job',
  open_balance: 'job',                openBalance: 'job',
  billed_total: 'job',                billedTotal: 'job',
  total_billable: 'job',              totalBillable: 'job',
  unbilled_amount: 'job',             unbilledAmount: 'job',
  outstanding_amount: 'job',          outstandingAmount: 'job',
  // A contract line's money (ar_billing_items.amount under its own name on
  // the Accounting board's contract lines, 2026-11-03).
  line_amount: 'job',                 lineAmount: 'job',
  amount_paid: 'job',                 amountPaid: 'job',
  paid_amount: 'job',                 paidAmount: 'job',
  invoiced_pre_tax: 'job',            invoicedPreTax: 'job',
  analytics_avg_job_value: 'job',     analyticsAvgJobValue: 'job',
  analytics_total_bid_dollars: 'job', analyticsTotalBidDollars: 'job',
  total_bid_dollars: 'job',           totalBidDollars: 'job',
  total_value: 'job',                 totalValue: 'job',
  // A change order's money and the award record's, wherever they travel (the
  // PM and Drafting cards, the Bid Summary, the award sheet). A change order's
  // own `delta` and its line items' `amount` are plain words, so they are
  // named through MONEY_SHAPES below.
  customer_portion: 'job',            customerPortion: 'job',
  approved_net: 'job',                approvedNet: 'job',
  freight_budget: 'job',              freightBudget: 'job',
  actual_freight: 'job',              actualFreight: 'job',
  sold_price_reduction: 'job',        soldPriceReduction: 'job',
  runner_up_price: 'job',             runnerUpPrice: 'job',
  ltl_quote: 'job',                   ltlQuote: 'job',
  freight_estimate: 'job',            freightEstimate: 'job',
  bid_release_freight_estimate: 'job', bidReleaseFreightEstimate: 'job',
  contract_price: 'job',              contractPrice: 'job',
  package_price_delta: 'job',         packagePriceDelta: 'job',
  // The job record's currency data fields (DataFieldRegistry), which ride in
  // a job's `details` under their own names.
  market_low: 'job',                  marketLow: 'job',
  market_high: 'job',                 marketHigh: 'job',
  pre_approval_reduction: 'job',      preApprovalReduction: 'job',
  // The legacy Monday bid amount, and the Estimator's price per production
  // day: job_data_fields money the database also holds back (2026-10-23).
  bid_amount: 'job',                  bidAmount: 'job',
  revenue_per_day: 'job',             revenuePerDay: 'job',
  // The Sales scorecard's figures (sales_scorecard(), 2026-11-04): what was
  // won and bid, against its goals and pace, and the open pipeline.
  won_dollars: 'job',                 wonDollars: 'job',
  goal_dollars: 'job',                goalDollars: 'job',
  pace_dollars: 'job',                paceDollars: 'job',
  avg_win_dollars: 'job',             avgWinDollars: 'job',
  pipeline_dollars: 'job',            pipelineDollars: 'job',
  quarter_goal_dollars: 'job',        quarterGoalDollars: 'job',
  quarter_won_dollars: 'job',         quarterWonDollars: 'job',
  week_goal_dollars: 'job',           weekGoalDollars: 'job',
  quote_dollars: 'job',               quoteDollars: 'job',
  marc2a_quote_dollars: 'job',        marc2aQuoteDollars: 'job',
  marc2a_won_dollars: 'job',          marc2aWonDollars: 'job',
  bid_dollars: 'job',                 bidDollars: 'job',
  won_per_year: 'job',                wonPerYear: 'job',
  bid_dollars_per_week: 'job',        bidDollarsPerWeek: 'job',
  bid_a_lot_dollars: 'job',           bidALotDollars: 'job',
  // The scorecard's revenue block: QuickBooks' income this year, its goal
  // and where it should be by now. At `cost` so a Sales lens can carry it,
  // and held behind board.sales.revenue by the route itself
  // (holdScorecardRevenue): only those who see the books, and Cassandra by
  // Drew's yes (2026-11-04), ever get it.
  revenue_dollars: 'cost',            revenueDollars: 'cost',
  revenue_goal_dollars: 'cost',       revenueGoalDollars: 'cost',
  revenue_pace_dollars: 'cost',       revenuePaceDollars: 'cost',
  next_year_revenue_goal_dollars: 'cost', nextYearRevenueGoalDollars: 'cost',
  revenue_this_year: 'cost',          revenueThisYear: 'cost',
  revenue_next_year: 'cost',          revenueNextYear: 'cost',

  // ── What it costs us, and what we make ──
  material_price: 'cost',             materialPrice: 'cost',
  bid_release_material_price: 'cost', bidReleaseMaterialPrice: 'cost',
  locked_material_price: 'cost',      lockedMaterialPrice: 'cost',
  profit_margin: 'cost',              profitMargin: 'cost',
  margin: 'cost',
  markup: 'cost',                     markup_pct: 'cost',
  unit_cost: 'cost',                  unitCost: 'cost',
  unit_price: 'cost',                 unitPrice: 'cost',
  price_per_unit: 'cost',             pricePerUnit: 'cost',
  delivered_cost: 'cost',             deliveredCost: 'cost',
  total_cost: 'cost',                 totalCost: 'cost',
  labor_cost: 'cost',                 laborCost: 'cost',
  material_cost: 'cost',              materialCost: 'cost',
  freight_cost: 'cost',               freightCost: 'cost',
  cost_per_piece: 'cost',             costPerPiece: 'cost',
  value_produced: 'cost',             valueProduced: 'cost',
  value_scrapped: 'cost',             valueScrapped: 'cost',
  value_lost: 'cost',                 valueLost: 'cost',
  scrap_value: 'cost',                scrapValue: 'cost',
  cumulative_value: 'cost',           cumulativeValue: 'cost',
  // A mix design's cost and its materials' last price (Elyce, 2026-10-20:
  // the Plant Lead saw the dollars on the batch sheet).
  cost_per_batch: 'cost',             costPerBatch: 'cost',
  cost_per_yard: 'cost',              costPerYard: 'cost',
  last_purchase_unit_cost: 'cost',    lastPurchaseUnitCost: 'cost',
  // What inventory costs us, every way the item, order and receipt rows say
  // it, and what the AI shopper found elsewhere (2026-10-23).
  avg_unit_cost: 'cost',              avgUnitCost: 'cost',
  avg_unit_cost_before: 'cost',       avgUnitCostBefore: 'cost',
  unit_cost_before: 'cost',           unitCostBefore: 'cost',
  unit_cost_after: 'cost',            unitCostAfter: 'cost',
  unit_cost_naive: 'cost',            unitCostNaive: 'cost',
  freight_unit_cost_before: 'cost',   freightUnitCostBefore: 'cost',
  freight_unit_cost_after: 'cost',    freightUnitCostAfter: 'cost',
  delivered_unit_cost: 'cost',        deliveredUnitCost: 'cost',
  last_delivered_unit_cost: 'cost',   lastDeliveredUnitCost: 'cost',
  delivered_cost_median_12m: 'cost',  deliveredCostMedian12m: 'cost',
  delivered_total: 'cost',            deliveredTotal: 'cost',
  backup_unit_cost: 'cost',           backupUnitCost: 'cost',
  backup_freight_amount: 'cost',      backupFreightAmount: 'cost',
  amazon_last_price: 'cost',          amazonLastPrice: 'cost',
  price_creep_pct: 'cost',            priceCreepPct: 'cost',
  pack_price: 'cost',                 packPrice: 'cost',
  list_price: 'cost',                 listPrice: 'cost',
  buybox_price: 'cost',               buyboxPrice: 'cost',
  freight_per_order: 'cost',          freightPerOrder: 'cost',
  freight_per_unit: 'cost',           freightPerUnit: 'cost',
  shipping_amount: 'cost',            shippingAmount: 'cost',
  free_over: 'cost',                  freeOver: 'cost',
  amazon_vs_delivered_pct: 'cost',    amazonVsDeliveredPct: 'cost',
  median_12m: 'cost',                 median12m: 'cost',
  creep_pct: 'cost',                  creepPct: 'cost',
  // The bid's cost build-up and the job's margin view.
  overhead_cost: 'cost',              overheadCost: 'cost',
  engineering_cost: 'cost',           engineeringCost: 'cost',
  project_engineering_cost: 'cost',   projectEngineeringCost: 'cost',
  mold_cost: 'cost',                  moldCost: 'cost',
  flatbed_shipping_cost: 'cost',      flatbedShippingCost: 'cost',
  freight_markup_override: 'cost',    freightMarkupOverride: 'cost',
  total_estimate: 'cost',             totalEstimate: 'cost',
  // The cost a send to production locks, and a bid's pricing dials.
  lock_material_price: 'cost',        lockMaterialPrice: 'cost',
  shipping_rate_override: 'cost',     shippingRateOverride: 'cost',
  anchors_profit_pct: 'cost',         anchorsProfitPct: 'cost',
  market_low_profit_override: 'cost', marketLowProfitOverride: 'cost',
  market_high_profit_override: 'cost', marketHighProfitOverride: 'cost',
  direct_cost: 'cost',                directCost: 'cost',
  margin_pct: 'cost',                 marginPct: 'cost',
  marcstone_absorbed: 'cost',         marcstoneAbsorbed: 'cost',
  all_in_cost: 'cost',                allInCost: 'cost',
  // The TABS rollup's dollars made and lost (the plant's Tracking tab).
  produced_value: 'cost',             producedValue: 'cost',
  rejected_value: 'cost',             rejectedValue: 'cost',

  // ── The company's own money ──
  bank_balance: 'company',            bankBalance: 'company',
  available_balance: 'company',       availableBalance: 'company',
  current_balance: 'company',         currentBalance: 'company',
  bill_amount: 'company',             billAmount: 'company',
  tax_due: 'company',                 taxDue: 'company',
  payroll_total: 'company',           payrollTotal: 'company',
  live_balance: 'company',            liveBalance: 'company',
  cost_usd: 'company',                costUsd: 'company',
  // The Production Status tab (the old PS Dashboard Excel): the month's
  // revenue, the break-even and the profit the bonus rides on. Its points stay;
  // they are the floor's own unit of work (2026-10-23).
  mtd_revenue: 'company',                         mtdRevenue: 'company',
  mtd_revenue_without_defects: 'company',         mtdRevenueWithoutDefects: 'company',
  mtd_revenue_without_overproduction: 'company',  mtdRevenueWithoutOverproduction: 'company',
  projected_month_end_revenue: 'company',         projectedMonthEndRevenue: 'company',
  projection_with_defects: 'company',             projectionWithDefects: 'company',
  projection_with_overproduction: 'company',      projectionWithOverproduction: 'company',
  projected_profit_amount: 'company',             projectedProfitAmount: 'company',
  projected_profit_pct: 'company',                projectedProfitPct: 'company',
  projected_pct_of_break_even: 'company',         projectedPctOfBreakEven: 'company',
  monthly_break_even: 'company',                  monthlyBreakEven: 'company',
  daily_break_even: 'company',                    dailyBreakEven: 'company',
  break_even_dollars: 'company',                  breakEvenDollars: 'company',
  dollars_per_point: 'company',                   dollarsPerPoint: 'company',
  dollars_per_manual_part: 'company',             dollarsPerManualPart: 'company',
  avg_hourly_rate: 'company',                     avgHourlyRate: 'company',
  // What an initiative saves or costs (the Initiatives board, open to all).
  money_expected: 'company',                      moneyExpected: 'company',
  money_actual: 'company',                        moneyActual: 'company',
};

/** The rung a named figure belongs to, or null when the name isn't a figure. */
export function moneyScopeOf(field: string): MoneyScope | null {
  return MONEY_FIELDS[field] ?? null;
}

/**
 * Lookups whose every VALUE is a figure, keyed by something that is not
 * (a job id): `valueByJob: { "<job id>": 18250 }`. The field list cannot see
 * a figure filed under an id, so these are named whole and answered EMPTY
 * ({}), never null — a screen that reads `valueByJob[id]` gets nothing to
 * show rather than a crash.
 */
export const MONEY_MAPS: Record<string, MoneyScope> = {
  valueByJob: 'job',                  value_by_job: 'job',
  // What the customer still owes after a lost-after-award, line by line.
  lost_after_award_items: 'job',      lostAfterAwardItems: 'job',
  // A bid version's stamped price build-up, and its per-part profit dials.
  totals_snapshot: 'cost',            totalsSnapshot: 'cost',
  part_profit_overrides: 'cost',      partProfitOverrides: 'cost',
  // The AI shopper's own side of a comparison (our unit, freight, delivered).
  our_side: 'cost',                   ourSide: 'cost',
  // The scorecard's revenue goal by year: { "2026": 2400000 } (held with
  // the revenue block, holdScorecardRevenue).
  revenue_by_year: 'cost',            revenueByYear: 'cost',
};

/**
 * Plain words that are figures because of the object they sit in. A change
 * order says its price in `delta` and its lines in `amount`, but those words
 * mean other things elsewhere (a drawing scenario's delta, a mold pick's
 * delta), so they count only inside an object carrying the mark (a change
 * order's number), and in everything nested under it (its line items).
 */
export const MONEY_SHAPES: readonly { marks: readonly string[]; fields: Readonly<Record<string, MoneyScope>> }[] = [
  { marks: ['co_number', 'coNumber'], fields: { delta: 'job', amount: 'job', price: 'job' } },
];

/**
 * Whether a figure is there, said without the figure. A screen that only
 * needs to know a price was SET (the shop kiosk receives only an order the
 * office priced) reads the flag; when the net holds the figure back it writes
 * the flag beside the null, so every answer that carries an order carries it.
 */
export const MONEY_PRESENCE: Readonly<Record<string, string>> = {
  total_cost: 'has_price',
};
